import { AppIcon, SearchBox, SmartListView } from '@/components';
import Loader from '@/components/Loader';
import PropTypes from 'prop-types';
import { useCallback, useMemo } from 'react';
import { useLanguage } from '@/localization/useLanguage';
import MemberListItem from './PartcipantListItem';
import { useChatInfo } from '../../../viewmodel/useChatInfo';
import './styles.scss';
import { InviteNewIcon } from '@/assets/images';

/**
 * Pure rendering component for Group Members list.
 * All data (participants, admin status) from useChatInfo ViewModel.
 */
const GroupParticipants = ({ groupId, groupName, participantsCount }) => {
   const { t } = useLanguage();
   const {
      totalParticipantsCount,
      participants,
      amIAdmin,
      searchText,
      setSearchText,
      handleOpenAddParticipant,
      handleExpandGroupParticipants,
   } = useChatInfo();

   const shouldSlice = participantsCount;
   const displayParticipants = shouldSlice ? participants.slice(0, participantsCount) : participants;
   const showSeeAll = participantsCount && participants.length > participantsCount;
   const showNoResults = searchText && participants.length === 0;

   const renderItem = useCallback(
      ({ item, index }) => <MemberListItem member={item} index={index} groupId={groupId} />,
      [groupId],
   );

   const keyExtractor = useCallback((item, index) => `${item.userId}-${index}`, []);

   const renderEmptyComponent = useMemo(() => {
      if (showNoResults) {
         return <div className="group-members-empty">{t('common.NO_PARTICIPANTS_FOUND')}</div>;
      }
      return null;
   }, [showNoResults, t]);

   const footerComponent = useMemo(() => {
      if (participants.length === 0 && !searchText) {
         return (
            <div className="group-members-loader ct-flex ct-jcCenter">
               <Loader size={28} />
            </div>
         );
      }
      return null;
   }, [participants.length, searchText]);

   return (
      <div className={`group-members-section ${!participantsCount ? 'full-view ct-flex ct-fcol' : ''}`}>
         <div className="group-members-header ct-flex ct-aiCenter ct-jcBetween">
            <span className="group-members-title">
               {t('chat.PARTICIPANTS_TITLE')}
               {totalParticipantsCount > 0 ? ` (${totalParticipantsCount})` : ''}
            </span>
         </div>

         {amIAdmin && shouldSlice && (
            <button
               type="button"
               className="add-participant-btn ct-flex ct-aiCenter ct-gap8"
               onClick={() => handleOpenAddParticipant(groupName)}
            >
               <span className="add-participant-icon ct-flexCenter">
                  <AppIcon size="xl" icon={<InviteNewIcon />} />
               </span>
               <span>{t('chat.ADD_PARTICIPANT')}</span>
            </button>
         )}

         {!participantsCount && (
            <div className="group-members-search">
               <SearchBox placeholder={t('common.SEARCH')} onChange={setSearchText} />
            </div>
         )}

         <div className="group-members-list ct-flex1 ct-flex ct-fcol" id="group-members-scroll">
            <SmartListView
               id="group-members-smart-list"
               data={displayParticipants}
               renderItem={renderItem}
               keyExtractor={keyExtractor}
               ListEmptyComponent={renderEmptyComponent}
               ListFooterComponent={footerComponent}
               itemHeight={52}
               enableVirtualization={!shouldSlice}
               overscan={5}
            />
         </div>

         {showSeeAll && (
            <button
               onClick={() => handleExpandGroupParticipants(groupName)}
               className="group-members-see-all"
               type="button"
            >
               {t('chat.SEE_ALL_PARTICIPANTS')}
            </button>
         )}
      </div>
   );
};

GroupParticipants.propTypes = {
   groupId: PropTypes.string.isRequired,
   groupName: PropTypes.string,
   participantsCount: PropTypes.number,
};

export default GroupParticipants;
